import type { CaseStudyMetric } from "@/data/caseStudies";

interface MetricTilesProps {
  metrics: CaseStudyMetric[];
  /** Larger tiles are used when a study has fewer than three verified metrics. */
  size?: "sm" | "md";
  className?: string;
}

/**
 * Compact, scannable result tiles for case study cards. Values are directional
 * unless a figure is genuinely verified, and every tile states its measurement
 * period or scope in small text.
 */
export function MetricTiles({ metrics, size = "sm", className = "" }: MetricTilesProps) {
  if (!metrics?.length) return null;
  const large = size === "md" || metrics.length < 3;

  return (
    <ul className={`grid gap-2 ${className}`}>
      {metrics.map((metric) => (
        <li
          key={metric.label}
          className="rounded-lg border border-border/60 bg-muted/40 px-3 py-2.5"
        >
          <div
            className={`font-extrabold text-primary leading-tight tracking-[-0.01em] ${
              large ? "text-xl sm:text-2xl" : "text-lg sm:text-xl"
            }`}
          >
            {metric.value}
          </div>
          <div className="text-[0.875rem] font-semibold text-foreground leading-snug mt-0.5">
            {metric.label}
          </div>
          <div className="text-[0.75rem] text-muted-foreground leading-[1.45] mt-1">
            {metric.note}
          </div>
        </li>
      ))}
    </ul>
  );
}
